<script lang="ts" setup>
import { type Game, GameAssetTypes } from '@game/types'
import { isEmpty } from 'es-toolkit/compat'
import type { ComponentPublicInstance } from 'vue'
import type { ImageOptions as NuxtImageOptions, ConfiguredImageProviders } from '@nuxt/image'
import { NuxtLink } from '#components'
import { computed, ref, watch, useMediaAutoplay, useCoreConfig, useGameOpeningHours, useGameType, useGameAssets, useHeroMedia, useI18n, useImage, useResizeObserver, useTemplateRef, useDeferredVideoAutoplay } from '#imports'

// @nuxt/image's signature hardcodes the default provider; widen it to opt into 'cloudflare'.
type GameTileImageOptions = Omit<NuxtImageOptions, 'provider'> & { provider?: keyof ConfiguredImageProviders }

interface HeroGameTileProps {
    game?: Game
    title?: string
    to?: string
    enableVideo?: boolean | string | undefined
    preset?: string
    videoSrc?: string
    videoId?: string
    videoPosterSrc?: string
    videoSplash?: string
}

const props = defineProps<HeroGameTileProps>()
const emit = defineEmits<{
    startGame: []
}>()

const game = computed((): Game | undefined => props.game)

const { company } = useCoreConfig()

const { isLiveCasino } = useGameType()
const { t, te } = useI18n()
const { isClosed } = useGameOpeningHours(game)
const { autoplayAllowed } = useMediaAutoplay()

// Setup Game Assets
const { assetUrl, videoAssetUrl, mediaVideoUrl } = useGameAssets()
const { getHeroGatewayUrl } = useHeroMedia()

const tileRef = ref<HTMLElement | ComponentPublicInstance>()
const tileWidth = ref(0)
const tileHeight = ref(0)

function setTileProperties(entries: ResizeObserverEntry[]) {
    const borderBox = entries[0]?.borderBoxSize?.[0]
    if (!borderBox) return

    tileWidth.value = Math.round(borderBox.inlineSize)
    tileHeight.value = Math.round(borderBox.blockSize)
}

// With a link target the tile is only that link — opening the game as well
// would fire two competing actions from one click.
function onTileClick(event: MouseEvent) {
    if (props.to) return

    event.preventDefault()
    emit('startGame')
}

const videoError = ref(false)
const videoLoaded = ref(false)
const showVideo = computed(() => props.enableVideo && autoplayAllowed.value && !videoError.value)

// Visible from first paint when conditions to render the media element are
// already satisfied. Opacity:0-during-init suppresses Cloudflare Stream's
// autoplay (the embed checks iframe visibility), so the previous @load-driven
// fade-in broke autoplay on initial SSR; on the second visit the cached embed
// + stronger origin-engagement signal hid the bug.
const videoConfirmed = computed(() => !!showVideo.value)
const posterFailed = ref(false)
const gameTileBg = computed(() => assetUrl(game.value, GameAssetTypes.TILE))
const featuredGameLogo = computed(() => assetUrl(game.value, GameAssetTypes.FEATURED_LOGO))
const gameLogo = computed(() => assetUrl(game.value, GameAssetTypes.LOGO))
const featuredLogoFailed = ref(false)
const gameHeroVideoPoster = computed(() => {
    return getHeroGatewayUrl(props.videoPosterSrc, props.preset) || assetUrl(game.value, GameAssetTypes.HERO_VIDEO_POSTER)
})

const gameHeroBg = computed(() => {
    if (!props.enableVideo) return gameTileBg.value

    return getHeroGatewayUrl(props.videoPosterSrc, props.preset) || gameTileBg.value
})

const gameHeroVideo = computed(() => {
    return props.videoSrc
        ? mediaVideoUrl(props.videoSrc, tileWidth.value, tileHeight.value)
        : videoAssetUrl(game.value, GameAssetTypes.HERO_VIDEO, tileWidth.value, tileHeight.value)
})

// A persistent tile (e.g. rotator) reuses the element across heroes, so bring
// the poster back when the video source changes until the new video loads.
watch(gameHeroVideo, () => {
    videoLoaded.value = false
})

const img = useImage() as (src: string, modifiers?: NuxtImageOptions['modifiers'], options?: GameTileImageOptions) => string // Used to generate video poster image

const gameTypeTranslation = computed<string>(() => {
    if (game.value?.game_type && te(`game.gameTile.${game.value.game_type}`)) {
        return t(`game.gameTile.${game.value.game_type}`)
    }

    return ''
})

const altTextTitle = computed(() => {
    let altText = ''

    if (game.value) {
        altText += game.value?.name

        if (gameTypeTranslation.value) altText += ` - ${gameTypeTranslation.value}`

        if (company?.websiteName) altText += ` - ${company.websiteName}`
    }

    return altText || props.title || undefined
})

const label = computed(() => {
    if (!game.value) return ''

    if (game.value.is_recommended) {
        return t('game.gameTile.recommended')
    }
    if (game.value.is_new) {
        return t('game.gameTile.new')
    }

    return ''
})

useResizeObserver(tileRef, setTileProperties)

const videoEl = useTemplateRef<HTMLVideoElement>('videoEl')
useDeferredVideoAutoplay(videoEl)
</script>

<template>
    <component
        :is="to ? NuxtLink : 'div'"
        ref="tileRef"
        class="hero-game-tile"
        :class="{ 'hero-game-tile--empty': isEmpty(game) }"
        :to="to"
        :role="to ? undefined : 'button'"
        @click="onTileClick"
    >
        <NuxtImg
            v-if="(videoPosterSrc || (!isEmpty(game) && gameTileBg)) && !(showVideo && videoLoaded)"
            provider="cloudflare"
            :preset="enableVideo && videoPosterSrc ? preset : 'gameservice'"
            class="hero-game-tile__bg fit-parent"
            width="345"
            quality="70"
            :src="posterFailed ? gameTileBg : gameHeroBg"
            :alt="altTextTitle"
            sizes="calc(100vw - 30px) md:50vw lg:500px"
            loading="eager"
            fetchpriority="high"
            @error="() => posterFailed = true"
        />
        <UIIframe
            v-if="showVideo && props.videoId"
            class="hero-game-tile__media"
            :class="{ 'hero-game-tile__media--confirmed': videoConfirmed }"
            :video-id="props.videoId"
            :title="altTextTitle"
            :fit-parent="true"
            :aspect-ratio="null"
            loading="eager"
            idle
            @load="videoLoaded = true"
        />
        <video
            v-else-if="showVideo && gameHeroVideo && tileWidth && tileHeight"
            ref="videoEl"
            class="hero-game-tile__media"
            :class="{ 'hero-game-tile__media--confirmed': videoConfirmed }"
            loop
            muted
            playsinline
            disableRemotePlayback
            decoding="async"
            preload="none"
            :poster="img(gameHeroVideoPoster, { width: 720, quality: 70 }, { preset: `${videoPosterSrc ? preset : 'gameservice'}`, provider: 'cloudflare' })"
            :src="gameHeroVideo"
            :title="altTextTitle"
            @loadeddata="videoLoaded = true"
            @error="() => videoError = true"
        />
        <NuxtImg
            v-if="videoSplash"
            provider="cloudflare"
            :preset="preset"
            class="hero-game-tile__logo"
            width="105"
            quality="70"
            :class="{ 'hero-game-tile__logo--video': showVideo }"
            :src="videoSplash"
            sizes="105px md:158px"
            :alt="`${game?.name || title || 'Video'} logo`"
        />
        <NuxtImg
            v-else-if="!isEmpty(game) && featuredGameLogo"
            provider="cloudflare"
            preset="gameservice"
            class="hero-game-tile__logo"
            width="105"
            quality="70"
            :class="{ 'hero-game-tile__logo--video': showVideo }"
            :src="featuredLogoFailed ? gameLogo : featuredGameLogo"
            sizes="105px md:158px"
            :alt="`${game?.name} logo`"
            @error="() => featuredLogoFailed = true"
        />

        <div
            v-if="!isEmpty(game) && !isClosed"
            class="hero-game-tile__label-wrap"
        >
            <!-- Todo: figure out at way to set game-tile label, since this is not set on game object -->
            <!-- Live casino label -->
            <p v-if="isLiveCasino(game.game_type, game.game_subtype)" class="hero-game-tile__live-label">
                Live
            </p>
            <!-- Hot or New label -->
            <p
                v-else-if="label"
                class="hero-game-tile__label"
                :class="{
                    'hero-game-tile__label--new': game.is_new,
                    'hero-game-tile__label--hot': game.is_recommended
                }"
                v-text="label"
            />
        </div>
        <LazyNimbusHeroJackpot
            v-if="game?.has_jackpot"
            :game="game"
            :is-closed="isClosed ?? undefined"
        />
    </component>
</template>

<style lang="scss" scoped>
.hero-game-tile {
    cursor: pointer;
    color: inherit;
    text-decoration: none;

    &,
    video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        object-fit: cover;
    }

    // Remove option to directly save images and videos.
    img,
    video {
        pointer-events: none;
    }

    &__bg {
        border-radius: inherit;
    }

    &__media {
        opacity: 0;
        transition: opacity 280ms ease-out;

        &--confirmed {
            opacity: 1;
        }
    }

    &__logo {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 60%;
        max-width: 240px;
        height: auto;
        max-height: 75%;
        object-fit: contain;

        @media (min-width: $screen-sm) {
            width: 40%;
            max-height: 75%;
        }

        @media (min-width: $screen-md) {
            width: 158px;
            max-height: unset;
        }

        &--video {
            position: absolute;
            width: 105px;
            top: -15px;
            left: -10px;
            // Fix blurry image when scaled with CSS
            transform: translateZ(0);

            @media (min-width: $screen-md) {
                width: 25%;
                top: -5%;
                left: -3%;
            }

            @media (min-width: $screen-xl) {
                width: 158px;
                top: -50px;
                left: -30px;
            }
        }
    }

    &__live-label {
        position: absolute;
        top: 0;
        right: 18px;
        padding: 2px 12px;
        border-radius: 0 0 $border-radius $border-radius;
        color: $game-tile-label-live-color;
        background: $game-tile-label-live-bg;
        font-weight: $text-bold;
    }

    &__label-wrap {
        position: absolute;
        top: 0;
        right: 0;
        width: 80px;
        height: 60px;
        overflow: hidden;
        border-radius: inherit;
    }

    &__label {
        position: absolute;
        top: -10px;
        right: -38px;
        z-index: 3;
        width: 100px;
        padding: 20px 0 0;
        transform: rotate(45deg);
        color: $game-tile-label-color;
        font-size: $font-size-base;
        font-weight: $text-heavy;
        text-align: center;
        text-transform: uppercase;

        &--hot {
            color: $game-tile-label-hot-color;
            background: $game-tile-label-hot-bg;
        }

        &--new {
            color: $game-tile-label-new-color;
            background: $game-tile-label-new-bg;
        }
    }
}
</style>
